import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router-dom'

import {Box, Card, Container} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import JumboTitle from '@progyny/components/JumboTitle'
import MenuList from '@progyny/components/MenuList'
import {pageTestIds} from '@progyny/const/pageTestIds'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {ProfRoutes} from '@progyny/routes/components/profile'
import {useMeStore} from '@progyny/store/meStore'

const ProfileIndexPage: FC<RouteComponentProps> = ({history}) => {
  const {productLine} = useProductLine()
  const {classes} = useStyles()
  const {t} = useTranslation()
  const {getPath} = useRoutes()
  const user = useMeStore((s) => s.user)

  // Block member-status and faq items for menopause and pregnancy product lines
  const isMenopauseOrPregnancy =
    productLine === 'menopause' || productLine === 'pregnancy_and_postpartum'

  const items = [
    {
      title: t('profile_personal_information'),
      caption: t('profile_manage_personal_details'),
      onClick: () =>
        history.push(
          `${getPath(RouteEnum.PROFILE)}${ProfRoutes.PERSONAL_INFO}`
        ),
    },
    ...(user?.member?.partnerDetails && !isMenopauseOrPregnancy
      ? [
          {
            title: t('profile_partner_information'),
            caption: t('profile_edit_personal_information'),
            onClick: () =>
              history.push(
                `${getPath(RouteEnum.PROFILE)}${ProfRoutes.PARTNER_DETAILS}`,
                {
                  fromProfile: true,
                }
              ),
          },
        ]
      : []),
    ...(user?.member?.contract?.reimbursementMode === 'bank_transfer' &&
    !isMenopauseOrPregnancy &&
    !!user?.member?.bankAccountDetails
      ? [
          {
            title: t('profile_payment_information'),
            caption: t('profile_edit_bank_details'),
            onClick: () =>
              history.push(
                `${getPath(RouteEnum.PROFILE)}${ProfRoutes.PAYMENT_DETAILS}`
              ),
          },
        ]
      : []),
    // Hide Explore journey for single productline
    ...(user?.member?.availableProductLines?.length! > 1
      ? [
          {
            title: t('navigation_manage_my_journeys'),
            caption: t('profile_update_journey_guidance'),
            onClick: () =>
              history.push(
                `${getPath(RouteEnum.PROFILE)}${ProfRoutes.EXPLORE_JOURNEY}`
              ),
          },
        ]
      : []),
    {
      title: t('profile_security'),
      caption: t('profile_change_password'),
      onClick: () =>
        history.push(`${getPath(RouteEnum.PROFILE)}${ProfRoutes.SECURITY}`),
    },
    // Hide Member Status for menopause and pregnancy
    ...(isMenopauseOrPregnancy
      ? []
      : [
          {
            title: t('profile_member_status'),
            caption: t('profile_enrollment_eligibility_details'),
            withBudget: true,
            onClick: () =>
              history.push(
                `${getPath(RouteEnum.PROFILE)}${ProfRoutes.MEMBER_STATUS}`
              ),
          },
        ]),
    {
      title: t('profile_faq'),
      caption: t('profile_faq_caption'),
      onClick: () =>
        history.push(`${getPath(RouteEnum.PROFILE)}${ProfRoutes.FAQ}`),
    },
  ]

  const mainItems = items.slice(0, -1)
  const faqItems = items.slice(-1)

  return (
    <Container
      data-testid={pageTestIds.profile}
      sx={{
        paddingTop: '64px',
        marginBottom: '120px',
      }}
    >
      <JumboTitle
        title={t('profile_index_manage_your_profile')}
        description={t('profile_index_update_personal_info')}
        disableContentWrapper
      >
        <Card
          className={classes.container}
          sx={{display: {xs: 'none', md: 'block'}}}
        >
          <MenuList items={items} data-testid='profile-menu' />
        </Card>

        <Box
          sx={{
            display: {xs: 'flex', md: 'none'},
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Card className={classes.mobileCard}>
            <MenuList items={mainItems} data-testid='profile-menu' />
          </Card>
          <Card className={classes.mobileCard}>
            <MenuList items={faqItems} />
          </Card>
        </Box>
      </JumboTitle>
    </Container>
  )
}

export {ProfileIndexPage}

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(8),
    maxWidth: 640,
    '&>div': {
      marginBottom: theme.spacing(4),
      '&>h4': {
        marginBottom: theme.spacing(1),
      },
      '&>a': {
        marginTop: theme.spacing(2),
        display: 'inline-block',
      },
    },
  },
  mobileCard: {
    padding: 0,
    borderRadius: 16,
    '& .MuiListItem-root': {
      padding: 0,
    },
    '& .MuiListItemButton-root': {
      padding: theme.spacing(2),
      paddingRight: theme.spacing(7),
    },
    '& .MuiListItemSecondaryAction-root': {
      right: theme.spacing(3),
    },
  },
}))
